import { AppLocation } from 'src/navigation/appLocations'
import * as Routes from 'src/navigation/urls'

/**
 * Expenses flat route tree (mirrors budgetsRouteTree); no tab bar, no flat
 * entity route — deviations documented in src/navigation/AGENTS.md.
 */

export interface ExpensesFamilyTabNode {
  /** Stable key used for active-tab detection. */
  key: string
  /** Translation key. (Unused for rendering — the family has no tab bar.) */
  labelSource: string
  /**
   * AppLocation for sidenav state. Optional: several legacy routes carry none
   * (Home fallback), preserved as-is rather than inventing new locations.
   */
  appLocation?: AppLocation
  /** Exact URL pattern, e.g. `/expenses/transactions`. */
  path: string
}

/** A legacy alias path that immediately redirects into Out-of-Pocket reports. */
export interface ExpensesAliasNode {
  /** Exact URL pattern, e.g. `/expenses/allReports`. */
  path: string
  /** `type` query param forwarded to the Out-of-Pocket reports page. */
  type: 'all' | 'yours'
}

export const expensesRouteTree = {
  family: {
    /** `/expenses` — redirects to the first section the user can see. */
    rootPath: Routes.expensesUrl(),
    /**
     * Legacy parity: `/expenses` redirects to Statements for bookkeepers, else
     * Card Transactions (data-dependent, so the page owns the choice).
     */
    redirectPrimaryTo: Routes.expensesExpenseReportsUrl(),
    redirectFallbackTo: Routes.expensesTransactionsUrl(),
    tabs: [
      {
        key: 'transactions',
        labelSource: 'Card Transactions',
        appLocation: AppLocation.CardTransactions,
        path: Routes.expensesTransactionsUrl(),
      },
      {
        // Card transactions awaiting approval. No AppLocation in the legacy
        // registry (resolves to the Home fallback) — preserved as-is.
        key: 'needsapproval',
        labelSource: 'Card Transactions',
        path: Routes.expensesNeedsApprovalUrl(),
      },
      {
        key: 'cycles',
        labelSource: 'Statements',
        appLocation: AppLocation.AccountingExpenseReports,
        path: Routes.expensesExpenseReportsUrl(),
      },
      {
        // Out-of-Pocket reports landing (reads a `type` query param). No
        // AppLocation in the legacy registry — preserved as-is.
        key: 'outofpocket',
        labelSource: 'Expenses',
        path: Routes.expensesOutOfPocketReportsUrl(),
      },
      {
        key: 'reimbursements',
        labelSource: 'Reimbursements',
        appLocation: AppLocation.AccountingReimbursements,
        path: Routes.reimbursementsUrl(),
      },
    ] as ExpensesFamilyTabNode[],
    /**
     * Legacy `allReports`/`yourReports` aliases redirect into Out-of-Pocket
     * reports; enumerated here so the family shell owns the redirect.
     */
    aliasRedirectTo: Routes.expensesOutOfPocketReportsUrl(),
    aliasPaths: [
      { path: Routes.expensesAllReportsUrl(), type: 'all' },
      { path: Routes.expensesYourReportsUrl(), type: 'yours' },
    ] as ExpensesAliasNode[],
  },
}

/**
 * Family root + every static tab/alias path; enumerated so each out-specifies
 * sibling `:id` routes at equal depth (never swallowed by the exact gate).
 */
export const expensesFamilyGatePaths: string[] = [
  expensesRouteTree.family.rootPath,
  ...expensesRouteTree.family.tabs.map((t) => t.path),
  ...expensesRouteTree.family.aliasPaths.map((a) => a.path),
]
